import { useMemo, useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/library/command";
import {
  useUniversityById,
  useUniversitySearch,
} from "@/features/universityAndCourse/api/useUniversities";

interface UniversityComboboxProps {
  value: string | undefined;
  onChange: (value: string) => void;
  hasError?: boolean;
  triggerId?: string;
  placeholder?: string;
}

/**
 * Searchable university picker that drives its options from the backend
 * (`?search=…`). Server-side because tenants commonly have 500+ rows; a
 * client-only filter would either ship the full list on every drawer
 * open or silently miss results past the first page.
 *
 * The selected label is resolved in priority order:
 *   1. From the current search results (fast path while browsing).
 *   2. From a one-shot GET /university/:id (edit-mode initial render
 *      when the selected university isn't in the current search page).
 */
export default function UniversityCombobox({
  value,
  onChange,
  hasError,
  triggerId,
  placeholder = "Select university",
}: UniversityComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  const { universities, isFetching } = useUniversitySearch(debounced);

  // Fall back to GET /:id only when the selected university isn't in the
  // current result page. Edit mode hits this on first render; once the
  // user opens the dropdown and types, the search list takes over.
  const selectedFromList = useMemo(
    () => universities.find((u: any) => String(u.id) === String(value)),
    [universities, value]
  );
  const { university: selectedFromApi } = useUniversityById(
    value && !selectedFromList ? value : null
  );
  const selectedLabel =
    selectedFromList?.name ?? selectedFromApi?.name ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={triggerId}
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            "bg-[#fcfcfc] border border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm tracking-[-0.56px] w-full inline-flex items-center justify-between gap-2 outline-none cursor-pointer",
            hasError
              ? "border-red-500 focus-visible:ring-red-500"
              : "focus-visible:ring-2 focus-visible:ring-[#cdd1d0]"
          )}
        >
          <span
            className={cn(
              "truncate text-left",
              selectedLabel ? "text-[#051912]" : "text-[#505e59]"
            )}
          >
            {selectedLabel ?? placeholder}
          </span>
          <ChevronDown className="size-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        // Match the trigger width so long names don't crash into the column.
        className="p-0 rounded-[12px] w-[var(--radix-popover-trigger-width)]"
      >
        {/* shouldFilter={false} hands filtering control to the backend. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search universities…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[280px]">
            {isFetching && universities.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-[#505e59]">
                <Loader2 className="size-3 animate-spin" />
                Searching…
              </div>
            ) : (
              <CommandEmpty>No university found.</CommandEmpty>
            )}
            {universities.map((u: any) => {
              const idStr = String(u.id);
              const isSelected = idStr === String(value);
              return (
                <CommandItem
                  key={idStr}
                  value={idStr}
                  onSelect={() => {
                    onChange(idStr);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="truncate">{u.name}</span>
                  {isSelected && (
                    <Check className="size-4 shrink-0 opacity-80" />
                  )}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
